import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _classCallCheck from '@babel/runtime/helpers/classCallCheck';
import _createClass from '@babel/runtime/helpers/createClass';
import { ExtensionType, DEFAULT_EXTENSION_PRIORITY, Tags, MarkGroup, NodeGroup } from '@remirror/core-constants';
export * from '@remirror/core-constants';
import { isString, deepMerge, isObject, sort, Cast, bool, entries, isFunction, isEqual, hasOwnProperty, isArray, isInstanceOf } from '@remirror/core-helpers';
export * from '@remirror/core-helpers';
import { PluginKey, EditorState } from 'prosemirror-state';
import _toConsumableArray from '@babel/runtime/helpers/toConsumableArray';
import { createDocumentNode, getPluginState, isMarkActive, isNodeActive } from '@remirror/core-utils';
export * from '@remirror/core-utils';
import { inputRules } from 'prosemirror-inputrules';
import { keymap } from 'prosemirror-keymap';
import { Schema } from 'prosemirror-model';
import { suggest } from 'prosemirror-suggest';
import _possibleConstructorReturn from '@babel/runtime/helpers/possibleConstructorReturn';
import _getPrototypeOf from '@babel/runtime/helpers/getPrototypeOf';
import _inherits from '@babel/runtime/helpers/inherits';
export * from '@remirror/core-types';

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(source, true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(source).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

/**
 * These are the default options merged into every extension.
 * They can be overridden.
 */
var defaultOptions = {
  extraStyles: '',
  extraAttrs: [],
  exclude: {
    inputRules: false,
    keymaps: false,
    pasteRules: false,
    plugin: false,
    styles: false,
    attributes: false,
    nodeView: false,
    ssr: false,
    suggesters: false
  }
};
/**
 * Extensions are fundamental to the way that Remirror works and they handle the
 * management of similar concerns.
 *
 * @remarks
 * They allows for grouping items that affect editor functionality.
 *
 * - How the editor displays certain content, i.e. **bold**, _italic_,
 *   **underline**.
 * - Which commands should be made available e.g. `actions.bold()` to make
 *   selected text bold.
 * - Check if a command is currently active or enabled e.g.
 *   `actions.bold.isActive()`.
 * - Register Prosemirror plugins, keymaps, input rules paste rules and custom
 *   nodeViews which affect the behaviour of the editor.
 *
 * There are three types of `Extension`.
 *
 * - `NodeExtension` - For creating Prosemirror nodes in the editor. See
 *   {@link NodeExtension}
 * - `MarkExtension` - For creating Prosemirror marks in the editor. See
 *   {@link MarkExtension}
 * - `Extension` - For behaviour which doesn't need to be displayed in the dom.
 *
 * The extension is an abstract class that should not be used directly but
 * rather extended to add the intended functionality.
 *
 * ```ts
 * interface AwesomeExtOptions extends BaseExtensionOptions {
 *   isAwesome: boolean;
 * }
 *
 * class AwesomeExt extends Extension<AwesomeExtOptions> {
 *   get name() {
 *     return 'awesome' as const
 *   }
 * }
 * ```
 */

var Extension =
/*#__PURE__*/
function () {
  /**
   * The options of this extension
   *
   * @remarks
   *
   * Options are passed in at instantiation and merged with the default options
   * of this extension.
   *
   */

  /**
   * The unique name of this extension.
   *
   * @remarks
   *
   * Every extension **must** have a name. Ideally the name should have a
   * distinct type to allow for better type inference for end users. By
   * convention the name should be `camelCased` and unique within your editor
   * instance.
   *
   * ```ts
   * class SimpleExtension extends Extension {
   *   get name() {
   *     return 'simple' as const;
   *   }
   * }
   * ```
   */

  /**
   * The prosemirror plugin key for this extension.
   */
  function Extension() {
    var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : Object.create(null);

    _classCallCheck(this, Extension);

    this.options = deepMerge(defaultOptions, _objectSpread({}, this.defaultOptions, {}, options));
    this.init();
  }
  /**
   * Allows for the addition of attributes to the defined schema.
   * This is only used in node and mark extensions.
   *
   * For now extraAttrs can only be optional
   */


  _createClass(Extension, [{
    key: "extraAttrs",
    value: function extraAttrs() {
      var fallback = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';

      if (this.type === ExtensionType.Plain) {
        throw new Error('Invalid use of extraAttrs within a plain extension.');
      }

      var extraAttrs = this.options.extraAttrs || [];
      var attrs = Object.create(null);
      var _iteratorNormalCompletion = true;
      var _didIteratorError = false;
      var _iteratorError = undefined;

      try {
        for (var _iterator = extraAttrs[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {
          var item = _step.value;

          if (Array.isArray(item)) {
            attrs[item[0]] = {
              default: item[1]
            };
          } else if (isString(item)) {
            attrs[item] = {
              default: fallback
            };
          } else {
            var name = item.name,
                def = item.default;
            attrs[name] = def ? {
              default: def
            } : {};
          }
        }
      } catch (err) {
        _didIteratorError = true;
        _iteratorError = err;
      } finally {
        try {
          if (!_iteratorNormalCompletion && _iterator.return != null) {
            _iterator.return();
          }
        } finally {
          if (_didIteratorError) {
            throw _iteratorError;
          }
        }
      }

      return attrs;
    }
    /**
     * Runs through the extraAttrs and retrieves the
     */

  }, {
    key: "getExtraAttrs",
    value: function getExtraAttrs(domNode) {
      if (this.type === ExtensionType.Plain) {
        throw new Error('Invalid use of extraAttrs within a plain extension.');
      }

      var extraAttrs = this.options.extraAttrs || [];
      var attrs = Object.create(null);
      var _iteratorNormalCompletion2 = true;
      var _didIteratorError2 = false;
      var _iteratorError2 = undefined;

      try {
        for (var _iterator2 = extraAttrs[Symbol.iterator](), _step2; !(_iteratorNormalCompletion2 = (_step2 = _iterator2.next()).done); _iteratorNormalCompletion2 = true) {
          var item = _step2.value;

          if (Array.isArray(item)) {
            // Use the default
            var _item = _slicedToArray(item, 3),
                name = _item[0],
                attributeName = _item[2];

            attrs[name] = attributeName ? domNode.getAttribute(attributeName) : undefined;
          } else if (isString(item)) {
            // Assume the name is the same
            attrs[item] = domNode.getAttribute(item);
          } else {
            var _name = item.name,
                getAttrs = item.getAttrs,
                fallback = item.default;
            attrs[_name] = getAttrs ? getAttrs(domNode) || fallback : fallback;
          }
        }
      } catch (err) {
        _didIteratorError2 = true;
        _iteratorError2 = err;
      } finally {
        try {
          if (!_iteratorNormalCompletion2 && _iterator2.return != null) {
            _iterator2.return();
          }
        } finally {
          if (_didIteratorError2) {
            throw _iteratorError2;
          }
        }
      }

      return attrs;
    }
    /**
     * This is a utility method to help avoid the need for defining a constructor
     * in sub classes.
     *
     * @remarks
     * When overriding this method make sure to call the super method.
     *
     * ```ts
     * class AwesomeExtension extends Extension {
     *   protected init() {
     *     super.init(); // Must be called
     *     this.doMyThing();
     *   }
     *
     *   private doMyThing() {
     *     // Secret sauce
     *   }
     * }
     * ```
     */

  }, {
    key: "init",
    value: function init() {
      this.pk = new PluginKey(this.name);
    }
    /**
     * This defines the type of the extension.
     *
     * @remarks
     * There are three types of extension:
     *
     * - `plain`
     * - `node` - see {@link NodeExtension}
     * - `mark` - see {@link MarkExtension}
     *
     * This identifier is used in the extension manager to separate marks from
     * nodes and to determine the functionality of each extension.
     */

  }, {
    key: "type",
    get: function get() {
      return ExtensionType.Plain;
    }
    /**
     * Tags help to categorize the behavior of an extension. This behavior is
     * later grouped in the extension manager and passed as `tag` to each
     * extension method. It can be used by commands that need to remove all
     * formatting and use the tag to identify which registered extensions are formatters.
     *
     * There are internally defined tags but it's also possible to define any
     * custom string as a tag. See {@link Tags}
     */

  }, {
    key: "tags",
    get: function get() {
      return [];
    }
    /**
     * Retrieves the plugin key which is used to uniquely identify the plugin
     * created by the extension.
     */

  }, {
    key: "pluginKey",
    get: function get() {
      if (this.pk) {
        return this.pk;
      }

      this.pk = new PluginKey(this.name);
      return this.pk;
    }
    /**
     * This determines the default options for the extension.
     *
     * @remarks
     * All non-required options that an extension uses should have a default options defined here.
     */

  }, {
    key: "defaultOptions",
    get: function get() {
      return {};
    }
    /**
     * `ExtensionOptions`
     *
     * This pseudo property makes it easier to infer generic types of this class.
     * @private
     */

  }]);

  return Extension;
}();

/**
 * Determines if the passed in extension is a any type of extension.
 *
 * @param extension - the extension to check
 */
var isExtension = function isExtension(extension) {
  return isObject(extension) && extension instanceof Extension;
};
/**
 * Checks whether the this is an extension and if it is a plain one
 *
 * @param extension - the extension to check
 */

var isPlainExtension = function isPlainExtension(extension) {
  return isExtension(extension) && extension.type === ExtensionType.Plain;
};
/**
 * Determines if the passed in extension is a mark extension. Useful as a type guard where a particular type of extension is needed.
 *
 * @param extension - the extension to check
 */

var isMarkExtension = function isMarkExtension(extension) {
  return isExtension(extension) && extension.type === ExtensionType.Mark;
};
/**
 * Determines if the passed in extension is a node extension. Useful as a type guard where a particular type of extension is needed.
 *
 * @param extension - the extension to check
 */

var isNodeExtension = function isNodeExtension(extension) {
  return isExtension(extension) && extension.type === ExtensionType.Node;
};

function ownKeys$1(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$1(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$1(source, true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$1(source).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

/**
 * Checks whether a given string is unique to the set. Add the name if it
 * doesn't already exist, or throw an error when `shouldThrow` is true.
 *
 * @param params - destructured params
 */
var isNameUnique = function isNameUnique(_ref) {
  var name = _ref.name,
      set = _ref.set,
      _ref$shouldThrow = _ref.shouldThrow,
      shouldThrow = _ref$shouldThrow === void 0 ? false : _ref$shouldThrow,
      _ref$type = _ref.type,
      type = _ref$type === void 0 ? 'extension' : _ref$type;

  if (set.has(name)) {
    var message = "There is a naming conflict for the name: ".concat(name, " used in this type: ").concat(type, ". Please rename to avoid runtime errors.");

    if (shouldThrow) {
      throw new Error(message);
    } else {
      console.error(message);
    }
  } else {
    set.add(name);
  }
};

/**
 * Get the params to which will be passed into the extension method call.
 *
 * @param extension - the extension to test.
 * @param params - the params without the type.
 */
var getParamsType = function getParamsType(extension, params) {
  if (isMarkExtension(extension)) {
    return {
      type: params.schema.marks[extension.name]
    };
  }

  if (isNodeExtension(extension)) {
    return {
      type: params.schema.nodes[extension.name]
    };
  }

  return Object.create(null);
};
/**
 * Checks to see if an optional property exists on an extension.
 *
 * @remarks
 *
 * This is used by the extension manager to build the:
 * - plugins
 * - keys
 * - styles
 * - inputRules
 * - pasteRules
 *
 * @param property - the extension property / method name
 */


var hasExtensionProperty = function hasExtensionProperty(property) {
  return function (extension) {
    return bool(extension[property]);
  };
};
/**
 * Generate all the action commands for usage within the UI.
 *
 * Typically actions are used to create interactive menus. For example a menu
 * can use a command to toggle bold formatting or to undo the last action.
 */

var createCommands = function createCommands(_ref2) {
  var extensions = _ref2.extensions,
      params = _ref2.params;

  var getItemParams = function getItemParams(extension) {
    return extension.commands(_objectSpread$1({}, params, {}, getParamsType(extension, params)));
  };

  var methodFactory = function methodFactory(method) {
    return function () {
      var view = params.view,
          getState = params.getState;
      view.focus();
      return method.apply(void 0, arguments)(getState(), view.dispatch, view);
    };
  };

  var items = Object.create(null);
  var names = new Set();
  extensions.filter(hasExtensionProperty('commands')).forEach(function (currentExtension) {
    var item = getItemParams(currentExtension);
    entries(item).forEach(function (_ref3) {
      var _ref4 = _slicedToArray(_ref3, 2),
          name = _ref4[0],
          command = _ref4[1];

      isNameUnique({
        name: name,
        set: names,
        shouldThrow: true
      });
      items[name] = {
        command: methodFactory(command),
        name: currentExtension.name
      };
    });
  });
  return items;
};

/**
 * Generate all the helpers from the extension list.
 *
 * Helpers are functions which enable extensions to provide useful
 * information or transformations to their consumers and other extensions.
 */
var createHelpers = function createHelpers(_ref5) {
  var extensions = _ref5.extensions,
      params = _ref5.params;

  var getItemParams = function getItemParams(extension) {
    return extension.helpers(_objectSpread$1({}, params, {}, getParamsType(extension, params)));
  };

  var items = Object.create(null);
  var names = new Set();
  extensions.filter(hasExtensionProperty('helpers')).forEach(function (currentExtension) {
    var item = getItemParams(currentExtension);
    Object.entries(item).forEach(function (_ref6) {
      var _ref7 = _slicedToArray(_ref6, 2),
          name = _ref7[0],
          helper = _ref7[1];

      isNameUnique({
        name: name,
        set: names,
        shouldThrow: true
      });
      items[name] = helper;
    });
  });
  return items;
};
/**
 * Keys for the methods available on an extension (useful for filtering)
 */

/**
 * Looks at the passed property and calls the extension with the required
 * parameters.
 *
 * @param property - the extension method to map
 * @param params - the params the method will be called with
 */
var extensionPropertyMapper = function extensionPropertyMapper(property, params) {
  return function (extension) {
    var extensionMethod = extension[property];

    if (!extensionMethod) {
      throw new Error('Invalid extension passed into the extension manager');
    }

    var getFn = function getFn() {
      if (isNodeExtension(extension)) {
        return extensionMethod.bind(extension)(_objectSpread$1({}, params, {
          type: Cast(params.schema.nodes[extension.name])
        }));
      }

      if (isMarkExtension(extension)) {
        return extensionMethod.bind(extension)(_objectSpread$1({}, params, {
          type: Cast(params.schema.marks[extension.name])
        }));
      }

      return extensionMethod.bind(extension)(Cast(params));
    };

    return Cast(getFn());
  };
};
/**
 * Converts an extension to its mapped value
 */

var convertToExtensionMapValue = function convertToExtensionMapValue(extension) {
  return isExtension(extension) ? {
    priority: DEFAULT_EXTENSION_PRIORITY,
    extension: extension
  } : _objectSpread$1({}, extension);
};
/**
 * Sorts and transforms extension map based on the provided priorities and
 * outputs just the extensions
 *
 * TODO Add a check for requiredExtensions and inject them automatically
 *
 * @param values - the extensions to transform as well as their priorities
 * @returns the list of extension instances sorted by priority
 */


var transformExtensionMap = function transformExtensionMap(values) {
  return sort(values.map(convertToExtensionMapValue), function (a, b) {
    return a.priority - b.priority;
  }).map(function (_ref8) {
    var extension = _ref8.extension;
    return extension;
  });
};
/**
 * Takes in an object and removes all function values.
 *
 * @remarks
 * This is useful for deep equality checks when functions need to be ignored.
 *
 * A current limitation is that it only dives one level deep. So objects with
 * nested object methods will retain those methods.
 *
 * @param obj - an object which might contain methods
 * @returns a new object without any of the functions defined
 */

var ignoreFunctions = function ignoreFunctions(obj) {
  var newObject = Object.create(null);

  for (var _i = 0, _Object$keys = Object.keys(obj); _i < _Object$keys.length; _i++) {
    var key = _Object$keys[_i];

    if (isFunction(obj[key])) {
      continue;
    }

    newObject[key] = obj[key];
  }

  return newObject;
};
/**
 * By default isActive should return false when no method specified.
 */

var defaultIsActive = function defaultIsActive() {
  return false;
};
/**
 * By default isEnabled should return true to let the code know that the
 * commands are available.
 */

var defaultIsEnabled = function defaultIsEnabled() {
  return true;
};
/**
 * Create the extension tags which are passed into each extensions method to
 * enable dynamically generated rules and commands.
 */

var createExtensionTags = function createExtensionTags(extensions) {
  var _general, _mark, _node;

  var general = (_general = {}, _defineProperty(_general, Tags.FormattingMark, []), _defineProperty(_general, Tags.FormattingNode, []), _defineProperty(_general, Tags.LastNodeCompatible, []), _defineProperty(_general, Tags.NodeCursor, []), _general);
  var mark = (_mark = {}, _defineProperty(_mark, MarkGroup.Alignment, []), _defineProperty(_mark, MarkGroup.Behavior, []), _defineProperty(_mark, MarkGroup.Color, []), _defineProperty(_mark, MarkGroup.FontStyle, []), _defineProperty(_mark, MarkGroup.Indentation, []), _defineProperty(_mark, MarkGroup.Link, []), _defineProperty(_mark, MarkGroup.Code, []), _mark);
  var node = (_node = {}, _defineProperty(_node, NodeGroup.Block, []), _defineProperty(_node, NodeGroup.Inline, []), _node);
  var _iteratorNormalCompletion = true;
  var _didIteratorError = false;
  var _iteratorError = undefined;

  try {
    var _loop = function _loop() {
      var extension = _step.value;

      if (isNodeExtension(extension)) {
        var group = extension.schema.group;
        node[group] = node[group] ? [].concat(_toConsumableArray(node[group]), [extension.name]) : [extension.name];
      } else if (isMarkExtension(extension)) {
        var _group = extension.schema.group;
        mark[_group] = mark[_group] ? [].concat(_toConsumableArray(mark[_group]), [extension.name]) : [extension.name];
      }

      extension.tags.forEach(function (tag) {
        general[tag] = general[tag] ? [].concat(_toConsumableArray(general[tag]), [extension.name]) : [extension.name];
      });
    };

    for (var _iterator = extensions[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {
      _loop();
    }
  } catch (err) {
    _didIteratorError = true;
    _iteratorError = err;
  } finally {
    try {
      if (!_iteratorNormalCompletion && _iterator.return != null) {
        _iterator.return();
      }
    } finally {
      if (_didIteratorError) {
        throw _iteratorError;
      }
    }
  }

  return {
    general: general,
    mark: mark,
    node: node
  };
};

function ownKeys$2(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$2(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$2(source, true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$2(source).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

/**
 * A class to manage the extensions and prosemirror interactions of our editor.
 *
 * @remarks
 *
 * The extension manager has three phases of Initialization:
 *
 * - Construction - This takes in all the extensions and creates the schema.
 *
 * ```ts
 * const manager = ExtensionManager.create([ new DocExtension(), new TextExtension(), new ParagraphExtension()])
 * ```
 *
 * - Initialize Getters - This connects the extension manager to the lazily
 *   evaluated `getState` method and the `portalContainer`. Once these are
 *   created and allows access to its data.
 *
 * ```ts
 * manager.init({ getState: () => state, portalContainer: new PortalContainer })
 *
 * manager.data.
 * ```
 *
 * - Initialize View - This connects the extension manager to the EditorView and
 *   creates the actions (which need access to the view).
 *
 * ```ts
 * manager.initView(new EditorView(...))
 * manager.data.actions
 * ```
 */
var ExtensionManager =
/*#__PURE__*/
function () {
  _createClass(ExtensionManager, null, [{
    key: "create",

    /**
     * A static method for creating a new extension manager.
     */
    value: function create(prioritizedExtensions) {
      var extensions = transformExtensionMap(prioritizedExtensions);
      return new ExtensionManager(extensions);
    }
    /**
     * Retrieve the latest state of the editor this manager is responsible for.
     * This is only available after the first Initialization.
     */

  }]);

  /**
   * Creates the extension manager which is used to simplify the management the
   * prosemirror editor.
   *
   * This should not be called directly if you want to use prioritized extensions.
   * Instead use `ExtensionManager.create`.
   */
  function ExtensionManager(extensions) {
    var _this = this;

    _classCallCheck(this, ExtensionManager);

    _defineProperty(this, "_initialized", false);

    _defineProperty(this, "initData", Object.create(null));

    _defineProperty(this, "getActions", function (name) {
      return _this.initData.actions[name];
    });

    _defineProperty(this, "getHelpers", function (name) {
      return _this.initData.helpers[name];
    });

    this.extensions = extensions; // Initialize the schema immediately since this doesn't ever change.

    this.initData.schema = this.createSchema(); // Options are cached here.

    this.initData.options = this.extensionOptions(); // Tags are stored here.

    this.initData.tags = createExtensionTags(this.extensions);
  }
  /**
   * Initialize the extension manager with important data.
   *
   * This is called by the view layer and provides
   */


  _createClass(ExtensionManager, [{
    key: "init",
    value: function init(_ref) {
      var getState = _ref.getState,
          portalContainer = _ref.portalContainer,
          getTheme = _ref.getTheme;

      if (this._initialized) {
        console.warn('This manager is already in use. Avoid using the same manager for more than one editor as this may cause problems.');
      }

      this.getState = getState;
      this.getTheme = getTheme;
      this.portalContainer = portalContainer;
      this._initialized = true;
      this.initData.styles = this.styles();
      this.initData.directPlugins = this.plugins();
      this.initData.nodeViews = this.nodeViews();
      this.initData.keymaps = this.keymaps();
      this.initData.inputRules = this.inputRules();
      this.initData.pasteRules = this.pasteRules();
      this.initData.suggestions = this.suggestions();
      this.initData.isActive = this.commandStatusCheck('isActive');
      this.initData.isEnabled = this.commandStatusCheck('isEnabled');
      this.initData.plugins = [].concat(_toConsumableArray(this.initData.directPlugins), [this.initData.suggestions, this.initData.inputRules], _toConsumableArray(this.initData.pasteRules), _toConsumableArray(this.initData.keymaps));
      this.initData.helpers = this.helpers();
      return this;
    }
    /**
     * Stores the editor view on the manager
     *
     * @param view - the editor view
     */

  }, {
    key: "initView",
    value: function initView(view) {
      var _this2 = this;

      this.initData.view = view;
      this.initData.actions = this.actions(_objectSpread$2({}, this.params, {
        view: view,
        isEditable: function isEditable() {
          return isFunction(view.props.editable) ? view.props.editable(_this2.getState()) : false;
        }
      }));
    }
    /* Public Get Properties */

    /**
     * Should be used to check whether the manager needs to be reinitialized.
     */

  }, {
    key: "createState",

    /* Public Methods */

    /**
     * Create the editor state from content passed to this extension manager.
     */
    value: function createState(_ref2) {
      var content = _ref2.content,
          doc = _ref2.doc,
          stringHandler = _ref2.stringHandler,
          fallback = _ref2.fallback;
      var _this$data = this.data,
          schema = _this$data.schema,
          plugins = _this$data.plugins;
      return EditorState.create({
        schema: schema,
        doc: createDocumentNode({
          content: content,
          doc: doc,
          schema: schema,
          stringHandler: stringHandler,
          fallback: fallback
        }),
        plugins: plugins
      });
    }
    /**
     * Collect data from all the extensions. This will be made available to the
     * consuming react component within the context data and also the child
     * renderProp function parameters.
     */

  }, {
    key: "extensionData",
    value: function extensionData() {
      var data = Object.create(null);
      var _iteratorNormalCompletion = true;
      var _didIteratorError = false;
      var _iteratorError = undefined;

      try {
        for (var _iterator = this.extensions[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {
          var extension = _step.value;
          data[extension.name] = extension.extensionData ? extensionPropertyMapper('extensionData', this.params)(extension) : {};
        }
      } catch (err) {
        _didIteratorError = true;
        _iteratorError = err;
      } finally {
        try {
          if (!_iteratorNormalCompletion && _iterator.return != null) {
            _iterator.return();
          }
        } finally {
          if (_didIteratorError) {
            throw _iteratorError;
          }
        }
      }

      return data;
    }
    /**
     * Checks whether two manager's are equal. Can be used to determine whether a
     * change in props has caused anything to actually change and prevent a
     * rerender.
     *
     * ExtensionManagers are equal when
     * - They have the same number of extensions
     * - Same order of extensions
     * - Each extension has the same options (ignoring methods)
     *
     * @param otherManager - the value to test against
     */

  }, {
    key: "isEqual",
    value: function isEqual$1(otherManager) {
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      if (!isExtensionManager(otherManager)) {
        return false;
      }

      if (this.extensions.length !== otherManager.extensions.length) {
        return false;
      }

      for (var ii = 0; ii <= this.extensions.length - 1; ii++) {
        var ext = this.extensions[ii];
        var otherExt = otherManager.extensions[ii];

        if (ext.constructor === otherExt.constructor && isEqual(ignoreFunctions(ext.options), ignoreFunctions(otherExt.options))) {
          continue;
        }

        return false;
      }

      return true;
    }
    /**
     * A handler which allows the extension to respond to each transaction without
     * needing to register a plugin.
     *
     * This is currently used in the collaboration plugin.
     */

  }, {
    key: "onTransaction",
    value: function onTransaction(params) {
      var _this3 = this;

      this.extensions.filter(hasExtensionProperty('onTransaction')).forEach(function (_ref3) {
        var onTransaction = _ref3.onTransaction;
        onTransaction(_objectSpread$2({}, params, {}, _this3.params, {
          view: _this3.data.view
        }));
      });
    }
    /**
     * Retrieve the state for a given extension name. This will throw an error if
     * the extension doesn't exist.
     *
     * @param name - the name of the extension
     */

  }, {
    key: "getPluginState",
    value: function getPluginState$1(name) {
      this.checkInitialized();
      var key = this.pluginKeys[name];

      if (!key) {
        throw new Error("Cannot retrieve state for an extension: ".concat(name, " which doesn't exist"));
      }

      return getPluginState(key, this.getState());
    }
    /**
     * Adjusts the rendered element based on the extension transformers.
     */

  }, {
    key: "ssrTransformer",
    value: function ssrTransformer(element) {
      var _this4 = this;

      return this.extensions.filter(hasExtensionProperty('ssrTransformer')).filter(function (extension) {
        return !extension.options.exclude.ssr;
      }).reduce(function (prevElement, extension) {
        return extension.ssrTransformer(prevElement, _this4.params);
      }, element);
    }
    /* Private Methods */

    /**
     * Checks to see if the extension manager has been initialized and throws if
     * not
     */

  }, {
    key: "checkInitialized",
    value: function checkInitialized() {
      if (!this._initialized) {
        throw new Error('Before using the extension manager it must be initialized with a portalContainer and getState');
      }
    }
    /**
     * Dynamically create the editor schema based on the extensions that have been
     * passed in.
     *
     * This is called as soon as the ExtensionManager is created.
     */

  }, {
    key: "createSchema",
    value: function createSchema() {
      return new Schema({
        nodes: this.nodes,
        marks: this.marks
      });
    }
    /**
     * Retrieve all the extension plugin keys
     */

  }, {
    key: "actions",

    /**
     * Create the actions which are passed into the render props.
     *
     * RemirrorActions allow for checking if a node / mark is active, enabled, and
     * also running the command.
     *
     * - `isActive` defaults to a function returning false
     * - `isEnabled` defaults to a function returning true
     */
    value: function actions(params) {
      var _this5 = this;

      // Will throw if not initialized
      this.checkInitialized();
      var extensions = this.extensions;
      var actions = Object.create(null); // Creates the methods that take in attrs and dispatch an action into the editor

      var commands = createCommands({
        extensions: extensions,
        params: params
      });
      Object.entries(commands).forEach(function (_ref4) {
        var _ref5 = _slicedToArray(_ref4, 2),
            commandName = _ref5[0],
            _ref5$ = _ref5[1],
            command = _ref5$.command,
            name = _ref5$.name;

        var isActive = _this5.initData.isActive[name] || defaultIsActive;
        var isEnabled = _this5.initData.isEnabled[name] || defaultIsEnabled;
        actions[commandName] = command;

        actions[commandName].isActive = function (attrs) {
          return isActive({
            attrs: attrs,
            command: commandName
          });
        };

        actions[commandName].isEnabled = function (attrs) {
          return isEnabled({
            attrs: attrs,
            command: commandName
          });
        };
      });
      return actions;
    }
  }, {
    key: "helpers",
    value: function helpers() {
      var helpers = Object.create(null);
      var methods = createHelpers({
        extensions: this.extensions,
        params: this.params
      });
      Object.entries(methods).forEach(function (_ref6) {
        var _ref7 = _slicedToArray(_ref6, 2),
            helperName = _ref7[0],
            helper = _ref7[1];

        helpers[helperName] = helper;
      });
      return helpers;
    }
    /**
     * Return the object of a boolean helper.
     *
     * This can be `isActive` or `isEnabled`.
     */

  }, {
    key: "commandStatusCheck",
    value: function commandStatusCheck(method) {
      var _this6 = this;

      // Will throw if not initialized
      this.checkInitialized();
      var isActiveMethods = Object.create(null);
      return this.extensions.filter(hasExtensionProperty(method)).reduce(function (acc, extension) {
        return _objectSpread$2({}, acc, _defineProperty({}, extension.name, extensionPropertyMapper(method, _this6.params)(extension)));
      }, isActiveMethods);
    }
    /**
     * Retrieve all plugins from the passed in extensions
     */

  }, {
    key: "plugins",
    value: function plugins() {
      this.checkInitialized();
      var plugins = [];
      var extensionPlugins = this.extensions.filter(hasExtensionProperty('plugin')).filter(function (extension) {
        return !extension.options.exclude.plugin;
      }).map(extensionPropertyMapper('plugin', this.params));
      extensionPlugins.forEach(function (plugin) {
        plugins.push(plugin);
      });
      return plugins;
    }
    /**
     * Retrieve the nodeViews created on the extensions for use within prosemirror
     * state
     */

  }, {
    key: "nodeViews",
    value: function nodeViews() {
      var _this7 = this;

      this.checkInitialized();
      var nodeViews = Object.create(null);
      return this.extensions.filter(hasExtensionProperty('nodeView')).filter(function (extension) {
        return !extension.options.exclude.nodeView;
      }).reduce(function (prevNodeViews, extension) {
        return _objectSpread$2({}, prevNodeViews, _defineProperty({}, extension.name, extensionPropertyMapper('nodeView', _this7.params)(extension)));
      }, nodeViews);
    }
    /**
     * Retrieve all keymaps (how the editor responds to keyboard commands).
     */

  }, {
    key: "keymaps",
    value: function keymaps() {
      this.checkInitialized();
      var extensionKeymaps = this.extensions.filter(hasExtensionProperty('keys')).filter(function (extension) {
        return !extension.options.exclude.keymaps;
      }).map(extensionPropertyMapper('keys', this.params));
      var mappedKeys = Object.create(null);
      var _iteratorNormalCompletion2 = true;
      var _didIteratorError2 = false;
      var _iteratorError2 = undefined;

      try {
        var _loop = function _loop() {
          var extensionKeymap = _step2.value;

          var _loop2 = function _loop2(key) {
            if (!hasOwnProperty(extensionKeymap, key)) {
              return "continue";
            }

            var oldCmd = mappedKeys[key];
            var newCmd = extensionKeymap[key];

            if (oldCmd) {
              newCmd = function newCmd(state, dispatch, view) {
                return oldCmd(state, dispatch, view) || extensionKeymap[key](state, dispatch, view);
              };
            }

            mappedKeys[key] = newCmd;
          };

          for (var key in extensionKeymap) {
            var _ret = _loop2(key);

            if (_ret === "continue") continue;
          }
        };

        for (var _iterator2 = extensionKeymaps[Symbol.iterator](), _step2; !(_iteratorNormalCompletion2 = (_step2 = _iterator2.next()).done); _iteratorNormalCompletion2 = true) {
          _loop();
        }
      } catch (err) {
        _didIteratorError2 = true;
        _iteratorError2 = err;
      } finally {
        try {
          if (!_iteratorNormalCompletion2 && _iterator2.return != null) {
            _iterator2.return();
          }
        } finally {
          if (_didIteratorError2) {
            throw _iteratorError2;
          }
        }
      }

      return [keymap(mappedKeys)];
    }
    /**
     * Retrieve all inputRules (how the editor responds to text matching certain
     * rules).
     */

  }, {
    key: "inputRules",
    value: function inputRules$1() {
      this.checkInitialized();
      var rules = [];
      var extensionInputRules = this.extensions.filter(hasExtensionProperty('inputRules')).filter(function (extension) {
        return !extension.options.exclude.inputRules;
      }).map(extensionPropertyMapper('inputRules', this.params));
      extensionInputRules.forEach(function (rule) {
        rules.push.apply(rules, _toConsumableArray(rule));
      });
      return inputRules({
        rules: rules
      });
    }
    /**
     * Retrieve all the options passed in for the extension manager
     */

  }, {
    key: "extensionOptions",
    value: function extensionOptions() {
      var options = Object.create(null);
      var _iteratorNormalCompletion3 = true;
      var _didIteratorError3 = false;
      var _iteratorError3 = undefined;

      try {
        for (var _iterator3 = this.extensions[Symbol.iterator](), _step3; !(_iteratorNormalCompletion3 = (_step3 = _iterator3.next()).done); _iteratorNormalCompletion3 = true) {
          var extension = _step3.value;
          options[extension.name] = extension.options;
        }
      } catch (err) {
        _didIteratorError3 = true;
        _iteratorError3 = err;
      } finally {
        try {
          if (!_iteratorNormalCompletion3 && _iterator3.return != null) {
            _iterator3.return();
          }
        } finally {
          if (_didIteratorError3) {
            throw _iteratorError3;
          }
        }
      }

      return options;
    }
    /**
     * Retrieve all pasteRules (rules for how the editor responds to pastedText).
     */

  }, {
    key: "pasteRules",
    value: function pasteRules() {
      this.checkInitialized();
      var pasteRules = [];
      var extensionPasteRules = this.extensions.filter(hasExtensionProperty('pasteRules')).filter(function (extension) {
        return !extension.options.exclude.pasteRules;
      }).map(extensionPropertyMapper('pasteRules', this.params));
      extensionPasteRules.forEach(function (rules) {
        pasteRules.push.apply(pasteRules, _toConsumableArray(rules));
      });
      return pasteRules;
    }
    /**
     * Extensions can register custom styles for the editor. This retrieves them.
     */

  }, {
    key: "styles",
    value: function styles() {
      this.checkInitialized();
      var extensionStyles = this.extensions.filter(hasExtensionProperty('styles')).filter(function (extension) {
        return !extension.options.exclude.styles;
      }).map(extensionPropertyMapper('styles', this.params));
      return extensionStyles;
    }
  }, {
    key: "suggestions",
    value: function suggestions() {
      this.checkInitialized();
      var suggestions = [];
      var extensionSuggesters = this.extensions.filter(hasExtensionProperty('suggestions')).filter(function (extension) {
        return !extension.options.exclude.suggestions;
      }).map(extensionPropertyMapper('suggestions', this.params));
      extensionSuggesters.forEach(function (suggester) {
        suggestions.push.apply(suggestions, _toConsumableArray(isArray(suggester) ? suggester : [suggester]));
      });
      return suggest.apply(void 0, suggestions);
    }
    /**
     * `Extensions`
     *
     * Type inference hack for the extensions union of an extension manager.
     * This is the only way I know to store types on a class.
     *
     * @internal
     * INTERNAL USE ONLY
     */

  }, {
    key: "initialized",
    get: function get() {
      return this._initialized;
    }
    /**
     * All the dynamically generated attributes provided by each extension.
     *
     * @remarks
     *
     * High priority extensions have preference over the lower priority
     * extensions.
     */

  }, {
    key: "attributes",
    get: function get() {
      var _this8 = this;

      var combinedAttributes = Object.create(null);
      this.extensions.filter(hasExtensionProperty('attributes')).filter(function (extension) {
        return !extension.options.exclude.attributes;
      }).map(function (extension) {
        return extension.attributes(_this8.params);
      }).reverse().forEach(function (attrs) {
        combinedAttributes = _objectSpread$2({}, combinedAttributes, {}, attrs, {
          class: (combinedAttributes.class || '') + (bool(attrs.class) ? attrs.class : '') || ''
        });
      });
      return combinedAttributes;
    }
    /**
     * Retrieve all the SSRComponent's from the extensions.
     * This is used to render the initial SSR output.
     */

  }, {
    key: "components",
    get: function get() {
      var components = Object.create(null);
      var _iteratorNormalCompletion4 = true;
      var _didIteratorError4 = false;
      var _iteratorError4 = undefined;

      try {
        for (var _iterator4 = this.extensions[Symbol.iterator](), _step4; !(_iteratorNormalCompletion4 = (_step4 = _iterator4.next()).done); _iteratorNormalCompletion4 = true) {
          var extension = _step4.value;

          if (!extension.options.SSRComponent || extension.options.exclude.ssr) {
            continue;
          }

          components[extension.name] = extension.options.SSRComponent;
        }
      } catch (err) {
        _didIteratorError4 = true;
        _iteratorError4 = err;
      } finally {
        try {
          if (!_iteratorNormalCompletion4 && _iterator4.return != null) {
            _iterator4.return();
          }
        } finally {
          if (_didIteratorError4) {
            throw _iteratorError4;
          }
        }
      }

      return components;
    }
    /**
     * Get the extension manager data which is stored after initializing.
     */

  }, {
    key: "data",
    get: function get() {
      if (!this._initialized) {
        throw new Error('Extension Manager must be initialized before attempting to access the data');
      }

      return this.initData;
    }
  }, {
    key: "options",
    get: function get() {
      return this.initData.options;
    }
    /**
     * Filters through all provided extensions and picks the nodes
     */

  }, {
    key: "nodes",
    get: function get() {
      var nodes = Object.create(null);
      var _iteratorNormalCompletion5 = true;
      var _didIteratorError5 = false;
      var _iteratorError5 = undefined;

      try {
        for (var _iterator5 = this.extensions[Symbol.iterator](), _step5; !(_iteratorNormalCompletion5 = (_step5 = _iterator5.next()).done); _iteratorNormalCompletion5 = true) {
          var extension = _step5.value;

          if (isNodeExtension(extension)) {
            var name = extension.name,
                schema = extension.schema;
            nodes[name] = schema;
          }
        }
      } catch (err) {
        _didIteratorError5 = true;
        _iteratorError5 = err;
      } finally {
        try {
          if (!_iteratorNormalCompletion5 && _iterator5.return != null) {
            _iterator5.return();
          }
        } finally {
          if (_didIteratorError5) {
            throw _iteratorError5;
          }
        }
      }

      return nodes;
    }
    /**
     * Filters through all provided extensions and picks the marks
     */

  }, {
    key: "marks",
    get: function get() {
      var marks = Object.create(null);
      var _iteratorNormalCompletion6 = true;
      var _didIteratorError6 = false;
      var _iteratorError6 = undefined;

      try {
        for (var _iterator6 = this.extensions[Symbol.iterator](), _step6; !(_iteratorNormalCompletion6 = (_step6 = _iterator6.next()).done); _iteratorNormalCompletion6 = true) {
          var extension = _step6.value;

          if (isMarkExtension(extension)) {
            var name = extension.name,
                schema = extension.schema;
            marks[name] = schema;
          }
        }
      } catch (err) {
        _didIteratorError6 = true;
        _iteratorError6 = err;
      } finally {
        try {
          if (!_iteratorNormalCompletion6 && _iterator6.return != null) {
            _iterator6.return();
          }
        } finally {
          if (_didIteratorError6) {
            throw _iteratorError6;
          }
        }
      }

      return marks;
    }
    /**
     * A shorthand method for retrieving the schema for this extension manager
     * from the data.
     */

  }, {
    key: "schema",
    get: function get() {
      return this.initData.schema;
    }
    /**
     * A shorthand getter for retrieving the tags from the extension manager.
     */

  }, {
    key: "tags",
    get: function get() {
      return this.initData.tags;
    }
    /**
     * A shorthand way of retrieving view.
     */

  }, {
    key: "view",
    get: function get() {
      return this.initData.view;
    }
    /* Private Get Properties */

    /**
     * Utility getter for accessing the schema params
     */

  }, {
    key: "params",
    get: function get() {
      return {
        tags: this.tags,
        schema: this.schema,
        portalContainer: this.portalContainer,
        getState: this.getState,
        getTheme: this.getTheme,
        getActions: this.getActions,
        getHelpers: this.getHelpers
      };
    }
  }, {
    key: "pluginKeys",
    get: function get() {
      var pluginKeys = Object.create(null);
      this.extensions.filter(function (extension) {
        return extension.plugin;
      }).forEach(function (_ref8) {
        var pluginKey = _ref8.pluginKey,
            name = _ref8.name;
        pluginKeys[name] = pluginKey;
      });
      return pluginKeys;
    }
  }]);

  return ExtensionManager;
}();
/**
 * Checks to see whether this is an extension manager
 *
 * @param value - the value to check
 */

var isExtensionManager = isInstanceOf(ExtensionManager);

/**
 * All mark extensions should be created from here.
 *
 * @remarks
 * A mark extension is based on the `Mark` concept from from within prosemirror
 * {@link https://prosemirror.net/docs/guide/#schema.marks}
 *
 * Marks are used to add extra styling or other information to inline content.
 * Mark types are objects much like node types, used to tag mark objects and
 * provide additional information about them.
 */

var MarkExtension =
/*#__PURE__*/
function (_Extension) {
  _inherits(MarkExtension, _Extension);

  function MarkExtension() {
    _classCallCheck(this, MarkExtension);

    return _possibleConstructorReturn(this, _getPrototypeOf(MarkExtension).apply(this, arguments));
  }

  _createClass(MarkExtension, [{
    key: "isActive",

    /**
     * Performs a default check to see whether the mark is active at the current
     * selection.
     *
     * @param params - see {@link @remirror/core-types#ExtensionManagerMarkTypeParams}
     */
    value: function isActive(_ref) {
      var getState = _ref.getState,
          type = _ref.type;
      return function () {
        return isMarkActive({
          state: getState(),
          type: type
        });
      };
    }
    /**
     * By default all marks extensions are set to be enabled. In your extension
     * this can be overridden and set to false based on the context.
     *
     * @param _ - see see {@link @remirror/core-types#ExtensionManagerMarkTypeParams}
     */

  }, {
    key: "isEnabled",
    value: function isEnabled(_) {
      return function () {
        return true;
      };
    }
  }, {
    key: "type",

    /**
     * Set's the type of this extension to be a `Mark`.
     *
     * @remarks
     *
     * This value is used by the predicates to check whether this is a mark / node
     * or plain extension.
     */
    get: function get() {
      return ExtensionType.Mark;
    }
    /**
     * The prosemirror specification which sets up the mark in the schema.
     *
     * @remarks
     *
     * The main difference between this and Prosemirror `MarkSpec` is that that
     * the `toDOM` method doesn't allow dom manipulation. You can only return an
     * array or string.
     *
     * For more advanced requirements, it may be possible to create a nodeView to
     * manage the dom interactions.
     */

  }]);

  return MarkExtension;
}(Extension);

/**
 * Defines an abstract class for creating extensions which can place nodes into the prosemirror state.
 *
 * @remarks
 * For more information see {@link https://prosemirror.net/docs/ref/#model.Node}
 */

var NodeExtension =
/*#__PURE__*/
function (_Extension) {
  _inherits(NodeExtension, _Extension);

  function NodeExtension() {
    _classCallCheck(this, NodeExtension);

    return _possibleConstructorReturn(this, _getPrototypeOf(NodeExtension).apply(this, arguments));
  }

  _createClass(NodeExtension, [{
    key: "isActive",
    value: function isActive(_ref) {
      var getState = _ref.getState,
          type = _ref.type;
      return function (_ref2) {
        var attrs = _ref2.attrs;
        return isNodeActive({
          state: getState(),
          type: type,
          attrs: attrs
        });
      };
    }
  }, {
    key: "isEnabled",
    value: function isEnabled(_) {
      return function () {
        return true;
      };
    }
  }, {
    key: "type",

    /**
     * Identifies this extension as a **NODE** type from the prosemirror terminology.
     */
    get: function get() {
      return ExtensionType.Node;
    }
    /**
     * The prosemirror specification which sets up the node in the schema.
     *
     * The main difference between this and Prosemirror `NodeSpec` is that that the `toDOM` method doesn't
     * allow dom manipulation. You can only return an array or string.
     *
     * For more advanced configurations, where dom manipulation is required, it is advisable to set up a nodeView.
     */

  }]);

  return NodeExtension;
}(Extension);

/**
 * The parent node in a Prosemirror Schema. A representation of the doc is required in all
 * editors
 *
 * @required
 * @builtin
 */
var DocExtension =
/*#__PURE__*/
function (_NodeExtension) {
  _inherits(DocExtension, _NodeExtension);

  function DocExtension() {
    _classCallCheck(this, DocExtension);

    return _possibleConstructorReturn(this, _getPrototypeOf(DocExtension).apply(this, arguments));
  }

  _createClass(DocExtension, [{
    key: "name",
    get: function get() {
      return 'doc';
    }
  }, {
    key: "defaultOptions",
    get: function get() {
      return {
        content: 'block+'
      };
    }
  }, {
    key: "schema",
    get: function get() {
      return {
        content: this.options.content
      };
    }
  }]);

  return DocExtension;
}(NodeExtension);

/**
 * The default text passed into the prosemirror schema.
 *
 * @builtin
 */

var TextExtension =
/*#__PURE__*/
function (_NodeExtension) {
  _inherits(TextExtension, _NodeExtension);

  function TextExtension() {
    _classCallCheck(this, TextExtension);

    return _possibleConstructorReturn(this, _getPrototypeOf(TextExtension).apply(this, arguments));
  }

  _createClass(TextExtension, [{
    key: "name",
    get: function get() {
      return 'text';
    }
  }, {
    key: "schema",
    get: function get() {
      return {
        group: 'inline'
      };
    }
  }]);

  return TextExtension;
}(NodeExtension);

export { DocExtension, Extension, ExtensionManager, MarkExtension, NodeExtension, TextExtension, isExtension, isExtensionManager, isMarkExtension, isNodeExtension, isPlainExtension };
//# sourceMappingURL=core.esm.js.map
